import {_, React, stringify} from '@api'
import {utils} from '@modules'
import {DashboardQueryParams, nav, PdfExportConfig, site, PdfLocalInfo, PdfSizeDocument, StoresInstance} from '@stores'
import {action, computed, observable, toJS} from 'mobx'
import {VipLocalStore} from '@modules'

export class PdfUndoStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
  }

  get pdf() { return this.stores.pdf}

  // region State

  @observable.shallow undoState: { queryParams: DashboardQueryParams, queryOptions, cardViewOptions, groupByOptions, otherDependencies } = {queryParams: null, queryOptions: null, cardViewOptions: null, groupByOptions: null, otherDependencies: null}

  @observable config: PdfExportConfig // next config - might be identical to one in pdf, might be changed but not commited by usr

  @observable isOpen: boolean = false

  @observable selectedPageSize = 'A4'

  @observable selectedLogo = 'Asics'

  @observable showCoverPage: boolean

  @action saveUndoState = () => {
    const {catalog, pdf} = this
    this.undoState = {
      queryParams: toJS(this.queryParamsForPdfUndo),
      queryOptions: toJS(pdf.searchPayload),
      groupByOptions: toJS(catalog.groupBy?.hierarchy),
      cardViewOptions: _.pickBy(toJS(catalog.cv.cardViewWithAttributes)),
      otherDependencies: toJS(this.otherPdfDependencies)
    }

    //console.log(toJS(this.undoState, {recurseEverything: true}))
  }

  @computed get selectedPdfDocument(): PdfSizeDocument {
    const {pdf, config: {document}, selectedPageSize} = this
    return _.find(document, d => d?.size === selectedPageSize) ?? pdf.A4_PAGE //_.isArray(document) ? document.find(d => d?.size === selectedPageSize) : document
  }

  @computed get queryParamsForPdfUndo() {
    //return _.pick(queryParams, ['filter', 'c_sort', 'cardView', 'catalog', 'groupBy', 'imageSource', '' ]
    const queryParams = _.omit(nav.queryParams, ['sidebar'])
    return {
      ...queryParams,
      ...this.queryParamsKeysToOmitExpandedField.reduce((acc, key) => {
        if (queryParams && queryParams[key]) Object.assign(acc, this.removeExpandedFromParams(queryParams, key))
        return acc
      }, {}) // ...this.removeExpandedFromParams(queryParams.filter)
    }
  }

  removeExpandedFromParams = (queryParams, key) => {
    const d = queryParams[key]
    return !_.isEmpty(d) ? {
      ...(() => {
        const filterParams = JSON.parse(Base64.decode(d))
        // Remove the expanded flag from filter params for YFTS-6240 and YFTS-6452
        const emptyExpandedGroups = []
        filterParams?.groups && Object.keys(filterParams.groups).forEach((key) => {
          // Remove the groups which are expanded but nothing is selected as they should not be considered
          // as change in filter
          if (filterParams.groups[key].expanded) {
            !(filterParams.groups[key].selected || []).length && emptyExpandedGroups.push(key)
            delete filterParams.groups[key].expanded
          }
        })
        emptyExpandedGroups.forEach((group) => {
          delete filterParams.groups[group]
        })
        // delete section as well
        delete filterParams.section
        if (_.isEmpty(filterParams?.groups)) {
          delete filterParams?.groups
        }
        return {[key]: !_.isEmpty(filterParams) ? Base64.encodeURI(stringify(filterParams)) : undefined}
      })()
    } : {}
  }

  @computed get queryParamsKeysToOmitExpandedField() {
    const queryParams = _.omit(nav?.queryParams, ['sidebar'])
    return ['filter'].concat(_.chain(queryParams).keys().filter(k => k.startsWith('filter')).value())
  }

  @computed get isQueryParamsDirty() {
    const {undoState: {queryParams}, queryParamsForPdfUndo} = this
    const compactDiff = _.reduce(utils.diff(queryParams, queryParamsForPdfUndo), (result, value, key) => {
      if (_.isArray(value)) {
        if (_.compact(value).length) result[key] = value
      } else {
        result[key] = value
      }
      return result
    }, {})
    return queryParams && !_.isEmpty(compactDiff)
  }

  @computed get isCardViewDirty() {
    const {cardViewOptions} = this.undoState
    const {catalog} = this

    return cardViewOptions && !_.isEqual(cardViewOptions, _.pickBy(catalog.cv.cardViewWithAttributes))
  }

  @computed get isGroupByQueryDirty() {
    const {groupByOptions} = this.undoState
    return groupByOptions && !_.isEqual(groupByOptions, this.catalog.groupBy?.hierarchy)
  }

  @computed get isQueryOptionsDirty() {
    const {pdf, undoState: {queryOptions}} = this
    const r = queryOptions && !_.isEqual(queryOptions, pdf.searchPayload)
    //r && console.log({queryOptions: toJS(queryOptions), payload: toJS(pdf.searchPayload)})
    return r
  }

  @computed get otherPdfDependencies() {
    const {catalog: {settings: {showCardLabels, imageRatio}, imageSource, esProductSortBy, groupBy}, selectedPdfDocument: {title, subject, subtitle}} = this
    return {showCardLabels, imageRatio, imageSource, esProductSortBy, groupBy_queryParams: groupBy.queryParams, title, subject, subtitle}
  }

  @computed get areOtherDependenciesDirty() {
    const {undoState: {otherDependencies}} = this
    return otherDependencies && !_.isEqual(otherDependencies, this.otherPdfDependencies)
  }

  @computed get isDirty() {
    const {config, isCardViewDirty, isQueryOptionsDirty, isQueryParamsDirty, areOtherDependenciesDirty, selectedPageSize, selectedLogo, pdf} = this
    return (!_.isEqual(toJS(config), toJS(pdf.config))) || isCardViewDirty || isQueryParamsDirty || isQueryOptionsDirty || areOtherDependenciesDirty || !_.isEqual(selectedPageSize, pdf.selectedPageSize) || selectedLogo !== pdf.selectedLogo
  }

  @action onChangeSectionCoverSelection = () => {
    const {showCoverPage, pdf, selectedPdfDocument: {title, subject, subtitle}} = this
    // config.sections.cover = !config.sections.cover
    if (showCoverPage && this.config.sections.cover === showCoverPage) {
      this.config.sections.cover = !showCoverPage
    }
    if (!showCoverPage && (title !== '' || subject !== '' || subtitle !== '')) {
      this.config.sections.cover = !showCoverPage
    }
    this.showCoverPage = !showCoverPage
  }

  // endregion

  // region Commit / Rollback

  @action commit = () => {
    const {
      isDirty, config, config: {
        queryLimit, showLogo, showFilters, showTotalCount, columns, imageRatio, layout, showHrGroupingLines, showVtGroupingLines, showCount, sections: {cover, products}
      }, selectedPageSize, selectedLogo, pdf
    } = this
    if (isDirty) {
      Object.assign(pdf.config, config)
      pdf.selectedPageSize = selectedPageSize
      pdf.selectedLogo = selectedLogo
      this.saveUndoState()
    }
    this.isOpen = false
    pdf.isFrozen = false
    pdf.document = null
    pdf.layoutRow = false
    pdf.limitedCount = 0
    pdf.config.sections.cover = this.showCoverPage

    const localInfo: PdfLocalInfo = {
      document: [this.selectedPdfDocument],
      config: {showLogo, showFilters, showTotalCount, columns, imageRatio, layout, showHrGroupingLines, showVtGroupingLines, showCount, queryLimit, sections: {cover, products}},
      selectedLogo
    }

    pdf.settings.undoState = localInfo
    // After rerender we should be frozen again after the doc is created
  }

  revertExpandedChanges = (finalQueryParams, key) => {
    // Add expanded groups in query params as they might have been changed
    // Add only expanded prop
    if (finalQueryParams && finalQueryParams[key]) {
      const finalQueryParamsFilter = JSON.parse(Base64.decode(finalQueryParams[key]))
      const navQueryParams = JSON.parse(Base64.decode(nav.queryParams[key]))
      navQueryParams?.groups && Object.keys(navQueryParams.groups).forEach(group => {
        navQueryParams.groups[group].expanded && Object.assign(finalQueryParamsFilter, {
          groups: {
            ...finalQueryParamsFilter?.groups,
            [group]: {
              ...(finalQueryParamsFilter?.groups ? finalQueryParamsFilter?.groups[group] ?? {} : {}),
              expanded: true
            }
          }
        })
      })
      // Add section as well as it might have changed
      if (navQueryParams?.section) {
        finalQueryParamsFilter.section = navQueryParams.section
      }
      finalQueryParams[key] = Base64.encode(stringify(finalQueryParamsFilter))
    }
  }

  @action rollback = () => {
    console.debug(`Reverting catalog changes for pdf...`)

    const {
      pdf, isQueryParamsDirty, areOtherDependenciesDirty, isCardViewDirty, isGroupByQueryDirty, groupBy, undoState: {
        groupByOptions, queryParams, otherDependencies: {groupBy_queryParams: {groupBy_orders}, showCardLabels}, cardViewOptions
      }, catalog, catalog: {groupBy: {catalogItemsGroup, hierarchyGroups}, cardView, settingsStore}
    } = this

    this.config = toJS(pdf.config)
    this.selectedPageSize = pdf.selectedPageSize
    this.selectedLogo = pdf.selectedLogo
    this.isOpen = false
    this.selectedPdfDocument.title = pdf.selectedPdfDocument.title
    this.selectedPdfDocument.subtitle = pdf.selectedPdfDocument.subtitle
    this.selectedPdfDocument.subject = pdf.selectedPdfDocument.subject
    this.showCoverPage = pdf.config.sections.cover

    if (isQueryParamsDirty) {
      const finalQueryParams = {search: queryParams.search ?? '', ...queryParams}
      // Add expanded groups in query params as they might have been changed
      // Add only expanded prop
      this.queryParamsKeysToOmitExpandedField?.forEach(f => this.revertExpandedChanges(finalQueryParams, f))
      console.debug(`NYI!  PDF rollback routing to`, toJS(finalQueryParams))
      // nav.routeTo({
      //   queryParams: finalQueryParams,
      //   verb: 'replace'
      // })

      //catalog.settingsStore.processQueryParams()
      // filter.deserializeFromQueryParams()
      // this.saveUndoState()
    }

    if (areOtherDependenciesDirty) {
      const orders = JSON.parse(atob(groupBy_orders))

      _.forEach(hierarchyGroups.filter(_.identity), (g) => g.orderBy.replace(orders[g.groupName]))
      catalogItemsGroup.orderBy.replace(orders[catalogItemsGroup.groupName])

      settingsStore.settings.showCardLabels = showCardLabels
      catalog.settings.imageRatio = this.config.imageRatio
      if (isGroupByQueryDirty) {
        groupBy.settings.hierarchy = groupByOptions ?? []
      }
    }

    if (isCardViewDirty) {
      _.keys(cardView).forEach(f => cardView[f] = cardViewOptions[f] ?? false)
    }

    // // Todo - Need a better way than this, how to know if query params have been processed again?  when()
    // setTimeout(() => {
    //   // this.saveUndoState()
    //   filter.updateFilterQueryParams()
    // }, 500)

    // We should still be frozen, just not dirty
  }

  // endregion

  // Keep track of our own copy of the report config so we can update it locally without having the pdf document react until the user stops typing or leaves the control (title, subtitle, etc)

  @action onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
  }

  /*@action toggleFreeze = () => {
    pdf.isFrozen = !pdf.isFrozen
  }*/

}

export class PdfDownloadStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
  }

  // region PDF Download

  @observable showDownloadPDFLink: boolean = false

  @action onDownload = async () => {
    this.stores.pdf.undo.commit()
    await this.downloadPdf()
    // this.isOpen = false
    // this.onReset()
  }

  @observable isLoading = false

  @action downloadPdf = async () => {
    this.isLoading = true
    site.pdfEmbedLink = true

    // const link = window.document.createElement('a')

    throw new Error('nyi - es')
    // const {apollo: {client}, pdf: {config: {queryLimit}, reportTemplate, documentProps}, catalog: {esProductSortBy}} = stores
    // const {data}                                                                                                          = await client.query({
    //                                                                                                                                              query:     gql.productQuery,
    //                                                                                                                                              variables: {
    //                                                                                                                                                where:  gql.catalogItemWhereClause,
    //                                                                                                                                                sortBy: esProductSortBy,
    //                                                                                                                                                first:  queryLimit == 0 ? undefined : queryLimit
    //                                                                                                                                              }
    //                                                                                                                                            })
    // const products                                                                                                        = data[gql.productsQueryField]
    // if (products) {
    //   returnChildren = (products.length > 0) ? reportTemplate.renderReportTemplate({data: [...products]}) : reportTemplate.renderReportTemplate({data: []})
    //   if (returnChildren) {
    //     MyDoc = getPdfDocument(returnChildren, documentProps)
    //     if (MyDoc) {
    //       const blob = await rendererPDF(MyDoc).toBlob()
    //       // create a blobURI pointing to our Blob
    //       link.href  = URL.createObjectURL(blob)
    //     }
    //   }
    // }
    // if (vlp.isVLPView) {
    //   link.download = `VLP_Export_PDF_${moment(new Date()).format('MMDDYYYYHHmm')}.pdf`
    // } else {
    //   link.download = `CB_PDF_${moment(new Date()).format('MMDDYYYYHHmm')}.pdf`
    // }
    // site.pdfEmbedLink = false
    // window.document.body.append(link)
    // link.click()
    // link.remove()
    // this.isLoading = false
  }
  //endregion
}


// WEBPACK FOOTER //
// ./src/modules/pdf/PdfUndoStore.tsx